import { InputRule, Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { NodeViewWrapper } from '@tiptap/react';
import {
  EMAIL_VARIABLE_BY_TOKEN,
  isKnownVariable,
} from '@/libs/correspondence';

/**
 * TipTap "variable" node — renders `{{token}}` placeholders as atomic chips
 * in the editor while serialising back to the literal `{{token}}` text in
 * the saved HTML. This is what lets the AI agent emit `{{anrede}}` in any
 * text field without knowing about TipTap, and lets the backend render
 * service substitute against `EMAIL_VARIABLE_BY_TOKEN` with a plain regex.
 *
 * On parse, a TipTap input rule + paste detection promote literal
 * `{{token}}` text to the chip node so AI-authored or pasted drafts look
 * the same as user-inserted ones.
 *
 * The `data-variable` attribute round-trips the token across save/load.
 * `renderHTML` writes the literal text `{{token}}` (NOT `<span>`) so the
 * backend resolver sees a plain `{{token}}` string regardless of who
 * authored it.
 */

interface VariableAttributes {
  token: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variableChip: {
      /** Inserts a variable chip at the current selection. */
      insertVariable: (token: string) => ReturnType;
    };
  }
}

export const VariableNode = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      token: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-variable') ?? '',
        renderHTML: (attrs: VariableAttributes) => ({
          'data-variable': attrs.token,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        // Round-trip from saved HTML: chip serialised below as
        // `<span data-variable="anrede">{{anrede}}</span>` parses back into
        // a node when loading drafts in the future. (We do not currently
        // round-trip the editor through HTML, but defending against it is
        // cheap and keeps the door open for persisted drafts.)
        tag: 'span[data-variable]',
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    // Save format = the literal token text. The backend resolver works on
    // raw `{{token}}` regardless of who authored it, so the saved HTML
    // stays valid input for `/v1/email-builder/render` AND for the AI
    // round-trip.
    const token = (node.attrs.token as string) || '';
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-variable': token }),
      `{{${token}}}`,
    ];
  },

  addCommands() {
    return {
      insertVariable:
        (token: string) =>
        ({ chain }) => {
          if (!isKnownVariable(token)) return false;
          return chain()
            .focus()
            .insertContent({ type: this.name, attrs: { token } })
            .insertContent(' ')
            .run();
        },
    };
  },

  addInputRules() {
    // Typing `{{anrede}}` (literal) auto-promotes to a chip — covers both
    // the user typing manually and the AI's literal output flowing in
    // through a future hydration path.
    const nodeType = this.type;
    return [
      new InputRule({
        find: /\{\{([a-z][a-z0-9._]*)\}\}$/,
        handler: ({ range, match, chain }) => {
          const token = match[1];
          if (!isKnownVariable(token)) return null;
          chain()
            .deleteRange(range)
            .insertContent({ type: nodeType.name, attrs: { token } })
            .run();
          return null;
        },
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableChip);
  },
});

/**
 * Visual chip rendered for each variable node inside the TipTap editor.
 * Read-only by design — selection + delete behave at the node boundary
 * because the node is `atom: true`.
 */
function VariableChip({ node }: NodeViewProps) {
  const token = (node.attrs.token as string) || '';
  const def = EMAIL_VARIABLE_BY_TOKEN.get(token);
  const label = def?.label ?? token;
  return (
    <NodeViewWrapper as="span" className="eb-var-chip" data-token={token}>
      <span className="eb-var-chip-marker">{'{{'}</span>
      <span className="eb-var-chip-label">{label}</span>
      <span className="eb-var-chip-marker">{'}}'}</span>
    </NodeViewWrapper>
  );
}
